/* daRouting -- THE ONE owner of "where does a D&A stream's forecast book?" (D&A routing, Part 2).
 *
 * companies.depreciation_is_line_key / amortization_is_line_key hold the operator's answer,
 * one per stream, in THREE states (the sweep_interest_is_line_key precedent):
 *   null                      never answered   -> the synthetic row (today's behaviour)
 *   the SENTINEL              'other_depreciation' / 'other_amortization' -- the synthetic's
 *                             own id: "keep a separate forecast row"      -> the synthetic row
 *   any other key             the historical IS leaf to book the forecast onto
 *
 * THE PREDICATE IS THREE-STATE ON PURPOSE. The sentinel is TRUTHY, so a two-state test
 * (`!!key`) would read "keep separate" as ROUTED and suppress the row the money is deposited
 * on -- the 09-15 implementation-sentinel defect verbatim, which failed silently.
 *
 * A KEY NAMING NO EMITTED LEAF FALLS BACK TO THE SYNTHETIC: suppressing the synthetic and
 * depositing on a key nothing renders would make the forecast D&A vanish from every row.
 *
 * Two readers, one rule: buildISLines (suppresses the routed synthetic) and runFullModel's
 * deposit overlay (books the forecast on the target). The Step-9 picker builds its options
 * from daTargetOptions, which is the client half of the deployed server guard's predicate
 * (statement_type 'IS', subsection 'ooe', the stream's stored role, leaf_key ?? is_line_key).
 *
 * NOT computeDA: its output keys stay other_depreciation / other_amortization, because
 * orchestrator.js feeds the accumulated-D&A roll from daResult.isLines.other_* directly.
 * Re-keying computeDA's output breaks that roll (36,471.87 by December; the balance check
 * now reports it). The D&A sums in lineRoles (EBITDA) and orchestrator (the CFS add-back) read
 * daLeafIds below -- every role-tagged D&A leaf plus both synthetics, a superset of the four literal
 * keys they used to name (W-075) -- and the accumulated roll reads computeDA's own two keys. All three
 * are PERMANENT and never narrowed: keep-separate is a legitimate state. */
import { leafKeyFor } from '../utils/leafKey';
import { SYNTHETIC_LINES, CATEGORY } from './lineRoles';

export const DA_STREAMS = [
  { synthetic: 'other_depreciation', role: 'depreciation', ctxKey: 'depreciationLineKey', column: 'depreciation_is_line_key' },
  { synthetic: 'other_amortization', role: 'amortization', ctxKey: 'amortizationLineKey', column: 'amortization_is_line_key' },
];

/* W-075 -- THE ONE "is this a D&A line?" test: subsection 'ooe' and a D&A stream's stored role.
 * daTargetOptions (the Step-9 routing candidates) and daLeafIds (the add-back / EBITDA set) both call
 * it, so the lines a forecast can be ROUTED onto and the lines that are ADDED BACK cannot disagree.
 * The role is an explicit stored value (inferIsMeta assigns it once); no name test (E1). */
export const DA_ROLES = DA_STREAMS.map(st => st.role);
export function isDaLine(x) {
  return !!x && x.subsection === CATEGORY.OOE && DA_ROLES.includes(x.role);
}

/* The ooe section total, from the layout owner (the total whose sumFromSubsection is 'ooe'). The
 * EMITTED skeleton's leaves carry role but not subsection (buildISLines emitLeaf), so on the skeleton
 * "in subsection ooe" is read from this total's sumOf, expanded through any display-group sub-totals. */
const OOE_TOTAL_ID = (SYNTHETIC_LINES.alwaysPresent.find(l => l.type === 'total' && l.sumFromSubsection === CATEGORY.OOE) || {}).id;
if (!OOE_TOTAL_ID) throw new Error('daRouting: no IS total with sumFromSubsection "ooe" in lineRoles');
function sectionLeafIds(isLines, totalId) {
  const byId = new Map((isLines || []).filter(l => l && l.id).map(l => [l.id, l]));
  const out = new Set(), seen = new Set();
  (function expand(id) {
    if (seen.has(id)) return;
    seen.add(id);
    const l = byId.get(id);
    if (l && l.type === 'total') (l.sumOf || []).forEach(expand);
    else if (l && !l.type) out.add(id);
  })(totalId);
  return out;
}

/* W-075 -- every D&A leaf on the income statement: each emitted leaf (no `type`) that passes isDaLine,
 * PLUS both synthetics, always (an absent key reads 0). The ids are unique, so a leaf is summed once
 * however it is named, and a combined "Depreciation & Amortization" line (one leaf, role
 * 'depreciation') is added back once. Readers: the EBITDA calc (lineRoles, handed in by computeIS) and
 * the CFS add-back (orchestrator buildCFSIsRef). It replaced their four literal keys -- other_depreciation,
 * other_amortization, oi_depreciation_expense, oi_amortization_expense -- which lost the add-back of
 * any D&A line named otherwise ("Depreciation" -> oi_depreciation) in every month it carried. It is a
 * SUPERSET of those four: the synthetics are always in it, and a line named "Depreciation Expense" /
 * "Amortization Expense" is inferred to exactly these roles. It widens; it never narrows.
 * NOT the accumulated-D&A roll (orchestrator buildBSIsRef): that reads computeDA's own output keys. */
export function daLeafIds(isLines) {
  const ids = new Set(DA_STREAMS.map(st => st.synthetic));
  const inOoe = sectionLeafIds(isLines, OOE_TOTAL_ID);
  (isLines || []).forEach(l => {
    if (l && l.id && !l.type && isDaLine({ role: l.role, subsection: inOoe.has(l.id) ? CATEGORY.OOE : l.subsection })) ids.add(l.id);
  });
  return [...ids];
}

/* The forecast target for one stream: the stored key when it names an emitted leaf and is
 * not the stream's sentinel; otherwise the synthetic. `emittedIds` is a Set of leaf ids. */
export function daRouteTarget(key, synthetic, emittedIds) {
  if (key == null || key === '' || key === synthetic) return synthetic;
  return emittedIds && emittedIds.has(key) ? key : synthetic;
}

/* The Step-9 candidates for one stream: this tenant's IS rows that pass isDaLine and carry the
 * stream's stored role. Mirrors the server guard's predicate exactly; no name test. */
export function daTargetOptions(glMappings, role) {
  return (glMappings || [])
    .filter(g => g.statement_type === 'IS' && isDaLine(g) && g.role === role)
    .map(g => ({ id: leafKeyFor(g), label: g.display_name || g.gl_name || leafKeyFor(g) }))
    .filter(o => o.id);
}
